import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { getTestTypes } from '../../utils/labUtils';
import DoctorAnalyticsCards from './DoctorAnalyticsCards';
import DoctorCharts from './DoctorCharts';

const DoctorAnalytics = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [doctorSession, setDoctorSession] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [testTypeNames, setTestTypeNames] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sessionData = localStorage.getItem('doctorSession');
    if (!sessionData) {
      navigate('/doctor/login');
      return;
    }
    try {
      const parsed = JSON.parse(sessionData);
      setDoctorSession(parsed);
      fetchSubmissions(parsed);
      loadTestTypeNames();
    } catch {
      localStorage.removeItem('doctorSession');
      navigate('/doctor/login');
    }
  }, []);

  const loadTestTypeNames = async () => {
    try {
      const types = await getTestTypes(true);
      const map = {};
      (types || []).forEach((t) => { map[t.id] = t.name || t.id; });
      setTestTypeNames(map);
    } catch (e) {
      console.error('Error loading test types:', e);
    }
  };

  const fetchSubmissions = async (doctor) => {
    setLoading(true);
    try {
      const { data: nameData, error: nameError } = await selectAll(() => supabase
        .from('submissions')
        .select('*')
        .ilike('doctor_name', `%${doctor.full_name}%`)
        .order('submitted_at', { ascending: false }));

      const { data: emailData, error: emailError } = await selectAll(() => supabase
        .from('submissions')
        .select('*')
        .eq('doctor_email', doctor.email)
        .order('submitted_at', { ascending: false }));

      if (nameError || emailError) throw nameError || emailError;

      const combined = [...(nameData || []), ...(emailData || [])];
      const unique = combined.filter((sub, i, self) => i === self.findIndex((s) => s.id === sub.id));
      setSubmissions(unique);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load submissions.', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Container maxW="7xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            <Button leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" onClick={() => navigate('/doctor/dashboard')}>
              Dashboard
            </Button>
            <Heading size="lg">Analytics</Heading>
          </HStack>
          <Text fontSize="sm" color="gray.500">Dr. {doctorSession?.full_name}</Text>
        </HStack>

        {/* Analytics Cards */}
        <DoctorAnalyticsCards submissions={submissions} testTypeNames={testTypeNames} doctorSession={doctorSession} />

        {/* Charts */}
        <DoctorCharts submissions={submissions} testTypeNames={testTypeNames} />
      </VStack>
    </Container>
  );
};

export default DoctorAnalytics;
